/**
 * `2o` — Usuários & convites.
 *
 * 🔒 **BR-18 é a forma desta tela.** O card de edição tem DUAS multi-seleções
 * independentes: *Clientes com acesso* e *Grupos de permissão*. Não são um par —
 * não existe "grupo X no cliente Y". O usuário carrega uma lista de cada, e a
 * permissão efetiva é a UNIÃO dos grupos aplicada a qualquer cliente que ele
 * alcance. É por isso que não há seletor de perfil em lugar nenhum da
 * plataforma, e por isso que estas duas listas não podem virar uma tabela de
 * pares — a estrutura da tela é o que impede alguém de implementar o oposto.
 *
 * ⚠️ **Essa mesma regra tem uma consequência que a iOX ainda não confirmou** (§9):
 * um usuário não pode ser Arquiteto no cliente A e somente-leitura no B. Eduardo
 * convidou nossa contestação. A tela diz isso em voz alta, embaixo da seleção de
 * grupos, para que a conversa aconteça olhando para o efeito.
 *
 * 🔒 **BR-08/09 — só por convite.** Não existe criação direta de usuário: a única
 * forma de uma conta passar a existir é o formulário de convite à direita, que o
 * Cognito envia. Nenhum campo de senha aparece aqui, e não deve aparecer.
 *
 * ⚠️ **A coluna `Convite` é uma junção por e-mail, não um campo do usuário.**
 * O §7.5 não põe status de convite em `Usuario` — são dois agregados, e as duas
 * rotas já existiam. Juntar por e-mail não inventa contrato; acrescentar um
 * `statusConvite` ao usuário inventaria.
 */
import { useState } from 'react'

import { ErroDeApi } from '@/api/clients/criarCliente'
import type { Usuario } from '@/api/contracts/administracao'
import {
  useClientesAdm,
  useConvidarUsuario,
  useCriarContaDeAcesso,
  useReenviarConvite,
  useConvites,
  useExcluirUsuario,
  useGrupos,
  useSalvarVinculosDoUsuario,
  useUsuariosAdm,
} from '@/api/queries/administracao'
import { Bloco, EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Badge, Botao, Campo, Entrada } from '@/components/primitivos'
import { usePermission } from '@/permissions/usePermission'

/**
 * ⚠️ `enviado` no contrato, `pendente` na tela.
 *
 * O shot escreve ATIVO / ACEITO / PENDENTE; o §7.5 enumera `enviado | aceito |
 * ativo`. É a mesma escala com um rótulo diferente no primeiro degrau — mapeado
 * aqui em vez de mexer no contrato, que espelha o backend.
 */
const STATUS_DO_CONVITE: Record<
  string,
  { rotulo: string; tom: 'sucesso' | 'atencao' | 'perigo' | 'neutro' }
> = {
  ativo: { rotulo: 'ativo', tom: 'sucesso' },
  aceito: { rotulo: 'aceito', tom: 'sucesso' },
  enviado: { rotulo: 'pendente', tom: 'atencao' },

  // ⚠️ Os dois abaixo faltavam, e o fallback pintava os dois de VERDE "ativo" —
  // exatamente a leitura oposta da verdade.
  //
  // Não apareciam porque o seed nunca os produzia. Com o Postgres eles passam a
  // acontecer sozinhos: `convites.expira_em` nasce `now() + interval '7 days'`, e
  // uma conta em FORCE_CHANGE_PASSWORD **não consegue** usar "esqueci minha
  // senha" (o Cognito recusa com NotAuthorizedException). Ou seja: o convidado
  // que demora uma semana trava, e só um administrador destrava — o pior momento
  // possível para a tela dizer que está tudo certo.
  expirado: { rotulo: 'expirado', tom: 'perigo' },
  revogado: { rotulo: 'revogado', tom: 'perigo' },
}

export function Usuarios() {
  const { editar } = usePermission('admin.usuarios')

  const usuarios = useUsuariosAdm()
  const convites = useConvites()
  const clientes = useClientesAdm()
  const grupos = useGrupos()

  const [selecionado, setSelecionado] = useState<string | undefined>()
  const [mostrarDesativados, setMostrarDesativados] = useState(false)

  if (usuarios.isPending) return <EsqueletoDeTabela rotulo="Carregando usuários…" colunas={4} />
  if (usuarios.isError) {
    return <EstadoDeErro erro={usuarios.error} aoTentarNovamente={() => void usuarios.refetch()} />
  }

  const usuarioSelecionado = usuarios.data.find((u) => u.email === selecionado)

  /**
   * 🔒 **Desativado não aparece — decisão do Fábio em 21/09/2026.**
   *
   * "Excluir usuário é desativar" (01/09) resolveu o BANCO: a linha fica, o
   * rastro de auditoria fica, e a V0015 nem concede DELETE. Mas a TELA seguia
   * listando quem foi desativado junto de quem trabalha, e sem distinção
   * nenhuma: a coluna Convite caía no fallback `'ativo'` e pintava a linha de
   * VERDE. Quem administra via "ativo" para uma conta que perdeu o acesso.
   *
   * O filtro é de EXIBIÇÃO, não de segurança (BR-17): o backend continua
   * servindo todo mundo, e é ele quem manda. Por isso os desativados continuam
   * alcançáveis num clique em vez de sumirem — esconder por padrão é o que a
   * operação pede; apagar da vista seria trocar um exagero pelo outro.
   */
  const desativados = usuarios.data.filter((u) => !u.ativo)
  const visiveis = mostrarDesativados ? usuarios.data : usuarios.data.filter((u) => u.ativo)

  /**
   * O status de convite do usuário, casado por e-mail. Ver o cabeçalho.
   *
   * 🔴 **`undefined` significa "ainda não sabemos", e é a correção de 09/09/2026.**
   * Isto era `convite?.status ?? 'ativo'` — um `??` que não distinguia "este
   * usuário não tem convite" de "a lista de convites ainda não chegou". A tabela
   * renderiza assim que os USUÁRIOS respondem, então, na janela entre uma
   * consulta e outra, TODA linha se pintava de verde "ativo".
   *
   * Ficava invisível enquanto as duas respondiam juntas. Deixou de ficar quando
   * `GET /convites` passou a consultar o user pool do Cognito: medido em
   * 09/09/2026, "ativo" aos 751ms e "pendente" só aos 3719ms — quase três
   * segundos afirmando o oposto da verdade, justamente na coluna que existe para
   * dizer quem ainda não entrou.
   *
   * O fallback para `ativo` continua — ele nasceu correto e seu caso é outro: um
   * usuário que existe e que a listagem de convites não devolveu entrou por algum
   * convite que já saiu da lista. O que mudou é que ele só vale DEPOIS de a
   * resposta chegar.
   */
  /** O convite da pessoa, casado por e-mail (CITEXT no banco, caixa livre aqui). */
  function conviteDe(usuario: Usuario) {
    return convites.data?.find((c) => c.email.toLowerCase() === usuario.email.toLowerCase())
  }

  /**
   * ⚠️ Se QUALQUER linha veio com o status morto, o pool não respondeu nesta
   * requisição — e aí uma pessoa ausente da lista também é desconhecida, não
   * "sem convite". Sem isto, uma queda do Cognito apareceria como dois rótulos
   * diferentes para o mesmo não-saber.
   *
   * Limite conhecido: uma resposta formada SÓ por `revogado` (o único status que
   * não depende do pool) não acusa a queda. É um canto estreito e erra para o
   * lado de "sem convite", que já não afirma que está tudo bem.
   */
  const poolIndisponivel = convites.data?.some((c) => !c.statusVivo) ?? false

  function statusDe(usuario: Usuario) {
    // Desativado vence qualquer status de convite: sem isto, quem perdeu o
    // acesso aparece com o status do convite — a leitura oposta da verdade, e a
    // razão de a linha precisar do selo ao ser revelada.
    if (!usuario.ativo) return { rotulo: 'desativado', tom: 'perigo' as const }
    if (convites.isPending || convites.isError) return undefined

    const convite = conviteDe(usuario)

    // 🔴 Aqui morava `?? 'ativo'`, e era o defeito que o Arimatéa sofreu em
    // 24/10/2026: o convite dele estava EXPIRADO e a tela dizia "Ativo", porque
    // quem é criado por CLI não tem linha em `administracao.convites` e sumia da
    // resposta. O fallback nasceu para "entrou por um convite que já saiu da
    // lista" — uma suposição que só vale para contas nascidas NA plataforma.
    // Adivinhar em verde é o único erro que esta coluna não pode cometer: ela
    // existe para dizer quem ainda não consegue entrar.
    if (!convite) {
      return poolIndisponivel
        ? { rotulo: 'indisponível', tom: 'neutro' as const }
        : { rotulo: 'sem convite', tom: 'neutro' as const }
    }

    // O status é o cache: o pool não respondeu. Mostrar cache como verdade é o
    // que escondeu um erro de digitação por seis semanas.
    if (!convite.statusVivo) return { rotulo: 'indisponível', tom: 'neutro' as const }

    // Catálogo ABERTO (BR-24): um valor que a tela não conhece vira neutro com o
    // próprio texto, nunca verde.
    return STATUS_DO_CONVITE[convite.status] ?? { rotulo: convite.status, tom: 'neutro' as const }
  }

  /**
   * Se o convite desta pessoa ainda PRECISA de reenvio.
   *
   * 🔴 Antes o botão era governado por `!usuario.cognitoSub`, e como TODA linha
   * de `administracao.usuarios` tem sub, ele nunca aparecia para ninguém — foi
   * por isso que o reenvio do Arimatéa precisou ser feito pelo CLI em 24/10/2026.
   * O raciocínio também estava invertido: `cognitoSub` diz que a CONTA EXISTE,
   * não que a pessoa ativou — e uma conta em `FORCE_CHANGE_PASSWORD` não
   * consegue usar "esqueci minha senha" (o Cognito recusa com
   * `NotAuthorizedException`). O gate escondia o botão exatamente de quem
   * depende dele.
   */
  function convitePendenteDe(usuario: Usuario) {
    const convite = conviteDe(usuario)
    if (!convite || !convite.statusVivo) return false
    return convite.status === 'enviado' || convite.status === 'expirado'
  }

  const nomeDoCliente = (id: string) => clientes.data?.find((c) => c.idCliente === id)?.nome ?? id
  const nomeDoGrupo = (id: string) => grupos.data?.find((g) => g.idGrupo === id)?.nome ?? id

  return (
    <section className="grid gap-4 xl:grid-cols-2">
      <div className="flex flex-col gap-4">
        <h2 className="type-h4 text-text-primary">Usuários &amp; convites</h2>

        <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-border-subtle border-b">
                {['Usuário', 'Convite', 'Clientes', 'Grupos'].map((rotulo) => (
                  <th
                    key={rotulo}
                    scope="col"
                    className="text-text-muted px-4 py-3 text-left type-eyebrow"
                  >
                    {rotulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visiveis.map((usuario) => {
                const status = statusDe(usuario)
                return (
                  <tr
                    key={usuario.email}
                    className={`border-border-subtle border-b last:border-b-0 ${
                      usuario.email === selecionado ? 'bg-navy-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => setSelecionado(usuario.email)}
                        className="text-left"
                      >
                        <span className="text-text-primary block type-sm font-semibold">
                          {usuario.nome}
                        </span>
                        <span className="text-text-muted block type-sm">{usuario.email}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {/* Enquanto os convites não chegam, um esqueleto no lugar
                          do selo: não saber precisa PARECER não saber. Um traço
                          seria uma afirmação a menos, mas ainda uma afirmação —
                          o cinza pulsante é o único que diz "estou carregando".

                          Com a consulta em erro fica o traço: aí não há mais o
                          que esperar, e um esqueleto eterno mentiria de outro
                          jeito. `EstadoDeErro` da tabela inteira seria demais —
                          as outras três colunas continuam corretas. */}
                      {status ? (
                        <Badge tom={status.tom}>{status.rotulo}</Badge>
                      ) : convites.isError ? (
                        <span className="text-text-muted" title="Não foi possível ler os convites">
                          —
                        </span>
                      ) : (
                        <Bloco className="h-6 w-24" />
                      )}
                    </td>
                    <td className="text-text-body px-4 py-3 align-top type-sm">
                      {/* "todos" quando o usuário alcança todos os tenants — é o
                          que o shot escreve, e é mais legível que repetir a
                          lista inteira em cada linha. */}
                      {clientes.data && usuario.clientes.length === clientes.data.length
                        ? 'todos'
                        : usuario.clientes.map(nomeDoCliente).join(' · ')}
                    </td>
                    <td className="text-text-body px-4 py-3 align-top type-sm">
                      {usuario.grupos.map(nomeDoGrupo).join(' · ')}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Só existe quando há o que revelar. A contagem no rótulo é o que
            impede o filtro de virar dado escondido: quem administra vê que
            existem contas desativadas sem precisar abri-las. */}
        {desativados.length > 0 && (
          <button
            type="button"
            className="text-text-muted hover:text-text-primary self-start type-sm underline"
            onClick={() => {
              // Ao esconder, a seleção vai junto: manter o painel de edição
              // aberto sobre uma linha que saiu da tabela deixa a tela dizendo
              // duas coisas diferentes ao mesmo tempo.
              if (mostrarDesativados && usuarioSelecionado && !usuarioSelecionado.ativo) {
                setSelecionado(undefined)
              }
              setMostrarDesativados(!mostrarDesativados)
            }}
          >
            {mostrarDesativados
              ? 'ocultar usuários desativados'
              : `mostrar ${desativados.length} usuário${desativados.length > 1 ? 's' : ''} desativado${desativados.length > 1 ? 's' : ''}`}
          </button>
        )}

        {usuarioSelecionado && (
          // 🔑 `key` no usuário: trocar de linha reinicia as seleções a partir
          // dos vínculos dele. Sem isso, marcar um grupo num usuário e clicar em
          // outro salvaria a marcação na pessoa errada.
          <CardDeEdicao
            key={usuarioSelecionado.email}
            usuario={usuarioSelecionado}
            editar={editar}
            convitePendente={convitePendenteDe(usuarioSelecionado)}
            clientes={(clientes.data ?? []).map((c) => ({ id: c.idCliente, rotulo: c.nome }))}
            grupos={(grupos.data ?? []).map((g) => ({ id: g.idGrupo, rotulo: g.nome }))}
            aoFechar={() => setSelecionado(undefined)}
          />
        )}

        {!usuarioSelecionado && (
          <p className="text-text-muted type-sm">
            Selecione um usuário na tabela para editar seus clientes e grupos.
          </p>
        )}
      </div>

      <div>
        <h2 className="type-h4 text-text-primary">Convidar usuário</h2>
        {editar ? (
          <FormularioDeConvite
            clientes={(clientes.data ?? []).map((c) => ({ id: c.idCliente, rotulo: c.nome }))}
            grupos={(grupos.data ?? []).map((g) => ({ id: g.idGrupo, rotulo: g.nome }))}
          />
        ) : (
          <EstadoVazio titulo="Você não tem permissão para convidar usuários." />
        )}
      </div>
    </section>
  )
}

/**
 * O `codigo` de negócio que a API devolve, quando devolve.
 *
 * `ErroDeApi.corpo` é o body já parseado — é onde o envelope
 * `{ codigo, message }` chega. A tela quase nunca ramifica por `codigo` (ela
 * renderiza `message`), mas aqui o código decide QUAL AÇÃO oferecer, e oferecer a
 * errada é pior que não oferecer nenhuma.
 */
function contaJaExiste(erro: unknown): boolean {
  if (!(erro instanceof ErroDeApi)) return false
  const corpo = erro.corpo as { codigo?: string } | null
  return corpo?.codigo === 'CONTA_JA_EXISTE'
}

type Opcao = { id: string; rotulo: string }

function CardDeEdicao({
  usuario,
  clientes,
  grupos,
  editar,
  convitePendente,
  aoFechar,
}: {
  usuario: Usuario
  clientes: Opcao[]
  grupos: Opcao[]
  editar: boolean
  /** Se o convite ainda precisa de reenvio — ver `convitePendenteDe`. */
  convitePendente: boolean
  aoFechar: () => void
}) {
  const salvar = useSalvarVinculosDoUsuario()
  const excluir = useExcluirUsuario()
  const reenviar = useReenviarConvite()

  const [clientesSelecionados, setClientes] = useState<string[]>(usuario.clientes)
  const [gruposSelecionados, setGrupos] = useState<string[]>(usuario.grupos)

  return (
    <div className="border-border-subtle bg-surface-card rounded-card border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="type-body-lg text-text-primary font-semibold">
          Editar usuário — {usuario.nome}
        </h3>
        {/* 🔒 **Desativar, não excluir.** A conta some do acesso e o rastro de
            auditoria fica — decisão do Fábio em 01/09/2026. O backend faz
            `UPDATE ... SET ativo = false` e a V0015 nem concede DELETE nesta
            tabela; apagar seria impossível de qualquer forma, porque
            `finops.oportunidades.criado_por` referencia o usuário sem CASCADE.

            O rótulo diz o que a operação FAZ. "excluir" prometia um
            desaparecimento que nunca aconteceu, e essa diferença importa para
            quem administra: a pessoa continua na tabela, marcada como inativa. */}
        <div className="flex flex-wrap items-center gap-2">
          {/* 🔴 O gate era `!usuario.cognitoSub` — e TODA linha de
              `administracao.usuarios` tem sub, então o botão nunca aparecia para
              ninguém. O reenvio do Arimatéa teve de sair pelo CLI em 24/10/2026
              por causa disto.

              `cognitoSub` diz que a CONTA EXISTE, não que a pessoa ativou. Quem
              está em `FORCE_CHANGE_PASSWORD` tem sub E precisa de reenvio — e não
              pode se virar com "esqueci minha senha", porque o Cognito recusa o
              reset nesse estado (`NotAuthorizedException`). Quem manda agora é o
              status do convite: `pendente` ou `expirado`. */}
          {editar && usuario.ativo && convitePendente && (
            <Botao
              variante="secundaria"
              icone="paper-plane-tilt"
              disabled={reenviar.isPending}
              onClick={() => reenviar.mutate({ email: usuario.email })}
            >
              {reenviar.isPending ? 'reenviando…' : 'reenviar convite'}
            </Botao>
          )}
          {editar && usuario.ativo && (
            <Botao
              variante="secundaria"
              icone="prohibit"
              disabled={excluir.isPending}
              onClick={() => excluir.mutate(usuario.email, { onSuccess: aoFechar })}
              className="text-danger-700"
            >
              desativar usuário
            </Botao>
          )}
        </div>
      </div>

      {reenviar.isSuccess && (
        <p role="status" className="text-success-700 mt-3 type-sm">
          Convite reenviado para {usuario.email}. Ele expira em 7 dias.
        </p>
      )}
      {reenviar.isError && <EstadoDeErro erro={reenviar.error} />}

      {/* 🔒 Duas seleções INDEPENDENTES, lado a lado. Ver o cabeçalho: elas não
          são um par, e a tela não pode sugerir que são. */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <MultiSelecao
          titulo="Clientes com acesso"
          rotuloDeBusca="buscar cliente…"
          opcoes={clientes}
          selecionados={clientesSelecionados}
          desabilitado={!editar}
          aoMudar={setClientes}
          rodape="seleção múltipla · lista de clientes onde o usuário atua"
        />
        <MultiSelecao
          titulo="Grupos de permissão"
          rotuloDeBusca="buscar grupo…"
          opcoes={grupos}
          selecionados={gruposSelecionados}
          desabilitado={!editar}
          aoMudar={setGrupos}
          rodape="seleção múltipla · permissões dos grupos são somadas"
        />
      </div>

      {/* ⚠️ §9 — a consequência da BR-18 que a iOX ainda não confirmou, dita
          onde ela acontece em vez de escondida num documento. */}
      <p className="text-text-muted mt-3 type-sm">
        Os grupos valem em <strong>todos</strong> os clientes marcados ao lado — não é possível dar
        um grupo a um cliente e outro grupo a outro. Essa consequência está em aberto com a iOX.
      </p>

      {editar && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Botao
            variante="primaria"
            disabled={salvar.isPending}
            onClick={() =>
              salvar.mutate({
                chave: usuario.email,
                clientes: clientesSelecionados,
                grupos: gruposSelecionados,
              })
            }
          >
            {salvar.isPending ? 'salvando…' : 'salvar vínculos'}
          </Botao>
          {salvar.isSuccess && (
            <span role="status" className="text-success-700 type-sm">
              vínculos salvos
            </span>
          )}
        </div>
      )}

      {salvar.isError && (
        <div className="mt-3">
          <EstadoDeErro erro={salvar.error} />
        </div>
      )}
      {excluir.isError && (
        <div className="mt-3">
          <EstadoDeErro erro={excluir.error} />
        </div>
      )}
    </div>
  )
}

/**
 * A multi-seleção com busca e "Todos" no topo, como o handoff descreve.
 *
 * ⚠️ **Sem Radix, ainda.** É uma lista de checkboxes com um campo de filtro
 * acima — o dropdown é persistente no shot, então não há popover para gerenciar,
 * nem foco a prender, nem portal. O gatilho para trocar por um combobox de
 * verdade é a lista de clientes ficar longa o bastante para precisar de
 * virtualização; hoje são dois.
 */
function MultiSelecao({
  titulo,
  rotuloDeBusca,
  rodape,
  opcoes,
  selecionados,
  desabilitado = false,
  aoMudar,
}: {
  titulo: string
  rotuloDeBusca: string
  rodape: string
  opcoes: Opcao[]
  selecionados: string[]
  /** Opcional: o formulário de convite só é renderizado com permissão de
   *  escrita, então lá a seleção nunca está desabilitada. */
  desabilitado?: boolean
  aoMudar: (proximos: string[]) => void
}) {
  const [busca, setBusca] = useState('')
  const visiveis = opcoes.filter((o) => o.rotulo.toLowerCase().includes(busca.toLowerCase()))
  const todosMarcados = opcoes.length > 0 && selecionados.length === opcoes.length

  return (
    <div>
      <h4 className="type-label text-text-primary">{titulo}</h4>

      <div className="relative mt-1.5">
        <i
          className="ph ph-magnifying-glass text-icon-default pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          aria-hidden="true"
        />
        <Entrada
          aria-label={`${titulo} — buscar`}
          placeholder={rotuloDeBusca}
          value={busca}
          disabled={desabilitado}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-9"
        />
      </div>

      <ul className="border-border-subtle rounded-field mt-2 max-h-56 overflow-y-auto border">
        <li className="border-border-subtle border-b">
          <label className="hover:bg-gray-50 flex cursor-pointer items-center gap-2 px-3 py-2 type-sm">
            <input
              type="checkbox"
              checked={todosMarcados}
              disabled={desabilitado}
              aria-label={`Todos — ${titulo}`}
              onChange={(e) => aoMudar(e.target.checked ? opcoes.map((o) => o.id) : [])}
              className="accent-iox-navy h-4 w-4"
            />
            Todos
          </label>
        </li>
        {visiveis.map((opcao) => {
          const marcado = selecionados.includes(opcao.id)
          return (
            <li key={opcao.id} className="border-border-subtle border-b last:border-b-0">
              <label
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 type-sm ${
                  marcado ? 'bg-navy-50' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  disabled={desabilitado}
                  aria-label={`${opcao.rotulo} — ${titulo}`}
                  onChange={(e) =>
                    aoMudar(
                      e.target.checked
                        ? [...selecionados, opcao.id]
                        : selecionados.filter((id) => id !== opcao.id),
                    )
                  }
                  className="accent-iox-navy h-4 w-4"
                />
                {opcao.rotulo}
              </label>
            </li>
          )
        })}
      </ul>

      <p className="text-text-muted mt-1.5 type-sm">{rodape}</p>
    </div>
  )
}

/**
 * 🔒 BR-08/09 — a ÚNICA porta de entrada de uma conta.
 *
 * O CTA é `enviar convite` e não há botão separado de "criar usuário", porque
 * criar usuário não é uma operação que exista nesta plataforma.
 */
function FormularioDeConvite({ clientes, grupos }: { clientes: Opcao[]; grupos: Opcao[] }) {
  const convidar = useConvidarUsuario()
  const criarConta = useCriarContaDeAcesso()
  const reenviarDoFormulario = useReenviarConvite()
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [clientesSelecionados, setClientes] = useState<string[]>([])
  const [gruposSelecionados, setGrupos] = useState<string[]>([])

  /**
   * Quem acabou de ser convidado. Guardado APÓS o formulário ser limpo, porque
   * o cadastro já está feito e o campo precisa esvaziar — mas se a criação da
   * conta falhar, é preciso saber para quem oferecer "tentar de novo".
   */
  const [ultimo, setUltimo] = useState<{ nome: string; email: string } | null>(null)

  return (
    <form
      className="border-border-subtle bg-surface-card rounded-card mt-4 flex flex-col gap-4 border p-5"
      onSubmit={(evento) => {
        evento.preventDefault()
        const convidado = { nome, email }
        convidar.mutate(
          {
            nome,
            email,
            grupos: gruposSelecionados,
            clientes: clientesSelecionados,
          },
          {
            onSuccess: () => {
              // O cadastro está feito — o formulário pode limpar. A criação da
              // conta de acesso segue em paralelo, e o resultado dela aparece
              // no bloco de status abaixo.
              setUltimo(convidado)
              criarConta.mutate(convidado)
              setNome('')
              setEmail('')
              setClientes([])
              setGrupos([])
            },
          },
        )
      }}
    >
      <Campo rotulo="Nome do convidado" obrigatorio>
        <Entrada
          required
          placeholder="nome completo"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
      </Campo>

      <Campo rotulo="E-mail" obrigatorio hint="convite enviado via Cognito">
        <Entrada
          required
          type="email"
          placeholder="nome@orgao.jus.br"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Campo>

      {/* 🔒 **Os vínculos entram no convite, não depois dele.**

          Convidar sem grupo e sem cliente cria alguém que autentica, vê os menus
          certos e toma 403 em toda tela de FinOps — porque a lista de clientes
          dela está vazia. Foi literalmente o incidente de 01/09/2026 com três
          contas da iOX. O backend grava as duas listas na MESMA transação do
          convite; a tela só precisa perguntar.

          🔒 BR-18: DUAS seleções independentes, como no card de edição. Elas não
          são um par, e a tela não pode sugerir que são. */}
      <div className="grid gap-4 md:grid-cols-2">
        <MultiSelecao
          titulo="Clientes do convidado"
          rotuloDeBusca="buscar cliente…"
          opcoes={clientes}
          selecionados={clientesSelecionados}
          aoMudar={setClientes}
          rodape="seleção múltipla · onde o convidado vai atuar"
        />
        <MultiSelecao
          titulo="Grupos do convidado"
          rotuloDeBusca="buscar grupo…"
          opcoes={grupos}
          selecionados={gruposSelecionados}
          aoMudar={setGrupos}
          rodape="seleção múltipla · permissões dos grupos são somadas"
        />
      </div>

      {/* Sem vínculo o convite ainda é aceito pelo backend — mas o convidado
          entra sem enxergar nada. Avisar antes é mais barato que diagnosticar
          um 403 depois. */}
      {(clientesSelecionados.length === 0 || gruposSelecionados.length === 0) && (
        <p className="text-attention-700 type-sm">
          Sem cliente ou sem grupo, o convidado consegue entrar mas não enxerga nenhuma tela.
        </p>
      )}

      {/* Sem campo "Mensagem" desde 14/10/2026: ele dizia acompanhar o e-mail de
          convite e não ia a lugar nenhum. O pool envia pelo e-mail padrão do Cognito
          (`COGNITO_DEFAULT`), que recusa corpo personalizado — só o SES aceita.
          Volta junto com a migração para o SES. */}
      <div className="flex flex-wrap items-center gap-3">
        <Botao
          variante="accent"
          type="submit"
          disabled={convidar.isPending || criarConta.isPending}
        >
          {convidar.isPending ? 'cadastrando…' : 'convidar'}
        </Botao>
      </div>

      {convidar.isError && <EstadoDeErro erro={convidar.error} />}

      {/* ⚠️ O estado é de DOIS passos, e a tela mostra os dois.
          Antes daqui, a `2o` dizia "convite enviado" assim que o cadastro
          gravava — e nenhum e-mail saía, porque ninguém criava a conta no
          Cognito. A tela prometia o que o sistema não fazia. */}
      {ultimo && criarConta.isPending && (
        <p role="status" className="text-text-muted type-sm">
          {ultimo.nome} foi cadastrado. Criando a conta de acesso…
        </p>
      )}

      {ultimo && criarConta.isSuccess && (
        <div role="status" className="text-success-700 type-sm">
          <p>
            Convite enviado para <strong>{ultimo.email}</strong>.
          </p>
          {/* A senha temporária do pool vale 7 DIAS, e uma conta que ainda não
              trocou a senha NÃO consegue usar "esqueci minha senha" — o Cognito
              recusa. Passado o prazo, só um Administrador destrava. */}
          <p className="text-text-muted">O convite expira em 7 dias.</p>
        </div>
      )}

      {ultimo && criarConta.isError && (
        <div className="border-attention-300 bg-attention-50 rounded-card border p-3">
          <p className="text-text-body type-sm">
            <strong>{ultimo.nome} foi cadastrado</strong> com os clientes e grupos escolhidos —
            isso está salvo. Mas a conta de acesso não foi criada, então a pessoa ainda não
            consegue entrar.
          </p>
          <EstadoDeErro erro={criarConta.error} />

          {/* ⚠️ A ação de reparo depende do MOTIVO da falha, e oferecer a errada
              faz o Administrador clicar num botão que não pode funcionar.

              `CONTA_JA_EXISTE` significa que a conta está lá — tentar criar de
              novo devolve o mesmo 409 para sempre. O que resolve é REENVIAR. Para
              qualquer outra falha (rede, permissão, Cognito fora), a conta
              provavelmente não nasceu e criar de novo é o certo. */}
          {contaJaExiste(criarConta.error) ? (
            <Botao
              variante="secundaria"
              icone="paper-plane-tilt"
              disabled={reenviarDoFormulario.isPending}
              onClick={() => reenviarDoFormulario.mutate({ email: ultimo.email })}
              className="mt-2"
            >
              {reenviarDoFormulario.isPending ? 'reenviando…' : 'reenviar convite'}
            </Botao>
          ) : (
            <Botao
              variante="secundaria"
              disabled={criarConta.isPending}
              onClick={() => criarConta.mutate(ultimo)}
              className="mt-2"
            >
              tentar enviar o convite de novo
            </Botao>
          )}

          {reenviarDoFormulario.isSuccess && (
            <p role="status" className="text-success-700 mt-2 type-sm">
              Convite reenviado para {ultimo.email}.
            </p>
          )}
          {reenviarDoFormulario.isError && <EstadoDeErro erro={reenviarDoFormulario.error} />}
        </div>
      )}
    </form>
  )
}
